import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Camera, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useMyWorkerRecord, useUpdateMyProfile, useMyTaxInfo, useMyCertifications } from "@/hooks/useWorkerPortal";
import { format } from "date-fns";

// ── Notification preference keys ──────────────────────────────────────────
const NOTIF_KEYS = [
  { key: "billing_cutoff",     label: "Billing cutoff reminders" },
  { key: "payment_processed",  label: "Payment processed" },
  { key: "reimbursement",      label: "Reimbursement approved/rejected" },
  { key: "new_message",        label: "New message from PM" },
  { key: "new_document",       label: "New document added" },
  { key: "schedule_changes",   label: "Schedule changes" },
];

function getStoredPrefs(workerId: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(`notif_prefs_${workerId}`);
    if (raw) return JSON.parse(raw);
  } catch {}
  // Default: all on
  return Object.fromEntries(NOTIF_KEYS.map(n => [n.key, true]));
}
function savePrefs(workerId: string, prefs: Record<string, boolean>) {
  localStorage.setItem(`notif_prefs_${workerId}`, JSON.stringify(prefs));
}

// ── Change Password Dialog ────────────────────────────────────────────────
function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClose = () => {
    setNewPw("");
    setConfirmPw("");
    onClose();
  };

  const handleSubmit = async () => {
    if (newPw.length < 8) return toast.error("Password must be at least 8 characters");
    if (newPw !== confirmPw) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPw });
      if (error) throw error;
      toast.success("Password updated successfully");
      handleClose();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Enter a new password for your account. Minimum 8 characters.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="new-pw">New Password</Label>
            <div className="relative">
              <Input
                id="new-pw"
                type={showNew ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="New password"
                className="pr-9"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => setShowNew(v => !v)}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-pw">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm-pw"
                type={showConfirm ? "text" : "password"}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Confirm new password"
                className="pr-9"
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => setShowConfirm(v => !v)}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {newPw && confirmPw && newPw !== confirmPw && (
            <p className="text-xs text-destructive">Passwords do not match</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !newPw || !confirmPw || newPw !== confirmPw}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Update Password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
const WorkerProfile = () => {
  const { data: worker, isLoading } = useMyWorkerRecord();
  const updateProfile = useUpdateMyProfile();
  const { data: taxInfo } = useMyTaxInfo(worker?.vendor_id || undefined);
  const { data: certs = [] } = useMyCertifications(worker?.id);

  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [ecName, setEcName] = useState("");
  const [ecPhone, setEcPhone] = useState("");
  const [ecRelation, setEcRelation] = useState("");
  const [dirty, setDirty] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({});
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (worker) {
      setPhone(worker.phone || "");
      setAddress(worker.address || "");
      setCity(worker.city || "");
      setState(worker.state || "");
      setZip(worker.zip || "");
      setEcName(worker.emergency_contact_name || "");
      setEcPhone(worker.emergency_contact_phone || "");
      setEcRelation(worker.emergency_contact_relation || "");
      setNotifPrefs(getStoredPrefs(worker.id));
    }
  }, [worker]);

  const handleSave = async () => {
    if (!worker) return;
    try {
      await updateProfile.mutateAsync({
        id: worker.id,
        phone: phone || null,
        address: address || null,
        city: city || null,
        state: state || null,
        zip: zip || null,
        emergency_contact_name: ecName || null,
        emergency_contact_phone: ecPhone || null,
        emergency_contact_relation: ecRelation || null,
      });
      setDirty(false);
      toast.success("Profile updated");
    } catch (err: any) {
      toast.error(err.message || "Failed to update");
    }
  };

  const markDirty = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setDirty(true);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !worker) return;
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) return toast.error("Only JPEG, PNG, or WebP images are allowed");
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be smaller than 5 MB");

    setPhotoUploading(true);
    try {
      const ext = file.name.split(".").pop() ?? "jpg";
      const path = `workers/${worker.id}/profile.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("worker-photos")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;

      const { data: { publicUrl } } = supabase.storage.from("worker-photos").getPublicUrl(path);
      await updateProfile.mutateAsync({ id: worker.id, profile_photo_url: publicUrl });
      toast.success("Profile photo updated");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to upload photo");
    } finally {
      setPhotoUploading(false);
      // Reset so the same file can be re-uploaded if needed
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleNotifToggle = (key: string, value: boolean) => {
    if (!worker) return;
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    savePrefs(worker.id, updated);
    toast.success(value ? "Notification enabled" : "Notification disabled");
  };

  const initials = worker
    ? `${(worker.first_name || worker.worker_name?.[0] || "W")[0]}${(worker.last_name || "P")[0]}`.toUpperCase()
    : "WP";

  if (isLoading) return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-40" />
      <Skeleton className="h-40" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">My Profile</h1>
        {dirty && (
          <Button onClick={handleSave} disabled={updateProfile.isPending}>
            {updateProfile.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Save Changes
          </Button>
        )}
      </div>

      {/* Photo */}
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar className="h-20 w-20">
            <AvatarImage src={worker?.profile_photo_url || undefined} />
            <AvatarFallback className="text-2xl bg-primary text-primary-foreground">{initials}</AvatarFallback>
          </Avatar>
          <button
            className="absolute bottom-0 right-0 bg-background border rounded-full p-1 hover:bg-muted transition-colors"
            onClick={() => photoInputRef.current?.click()}
            disabled={photoUploading}
            title="Change profile photo"
          >
            {photoUploading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Camera className="h-4 w-4" />}
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handlePhotoUpload}
          />
        </div>
        <p className="text-sm text-muted-foreground">Tap to change profile photo</p>
      </div>

      {/* Personal Info */}
      <Card>
        <CardHeader><CardTitle className="text-base">Personal Information</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First Name</Label><Input disabled value={worker?.first_name || worker?.worker_name?.split(" ")[0] || "—"} /></div>
            <div><Label>Last Name</Label><Input disabled value={worker?.last_name || "—"} /></div>
          </div>
          <div><Label>Phone</Label><Input value={phone} onChange={markDirty(setPhone)} placeholder="Enter phone number" /></div>
          <div><Label>Email</Label><Input disabled value={worker?.portal_email || "—"} /></div>
          <div><Label>Address</Label><Input value={address} onChange={markDirty(setAddress)} placeholder="Street address" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>City</Label><Input value={city} onChange={markDirty(setCity)} placeholder="City" /></div>
            <div><Label>State</Label><Input value={state} onChange={markDirty(setState)} placeholder="State" maxLength={2} /></div>
            <div><Label>ZIP</Label><Input value={zip} onChange={markDirty(setZip)} placeholder="ZIP" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader><CardTitle className="text-base">Emergency Contact</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Name</Label><Input value={ecName} onChange={markDirty(setEcName)} placeholder="Contact name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input value={ecPhone} onChange={markDirty(setEcPhone)} placeholder="Phone" /></div>
            <div><Label>Relationship</Label><Input value={ecRelation} onChange={markDirty(setEcRelation)} placeholder="e.g. Spouse" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Tax Info - Read Only */}
      <Card>
        <CardHeader><CardTitle className="text-base">Tax Information</CardTitle></CardHeader>
        <CardContent>
          {taxInfo ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">SSN</span>
                <span>***-**-{taxInfo.tax_id_last4 || "****"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax ID Type</span>
                <span className="uppercase">{taxInfo.tax_id_type || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Business Type</span>
                <span>{taxInfo.business_type || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">W9 on file</span>
                <span>
                  {taxInfo.submitted_by_vendor ? "✅" : "❌"}{" "}
                  {taxInfo.locked_at ? format(new Date(taxInfo.locked_at), "MMM d, yyyy") : ""}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No tax information on file. Contact your administrator.</p>
          )}
        </CardContent>
      </Card>

      {/* Certifications */}
      <Card>
        <CardHeader><CardTitle className="text-base">Skills & Certifications</CardTitle></CardHeader>
        <CardContent>
          {certs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No certifications on file.</p>
          ) : (
            <div className="space-y-2">
              {certs.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <span>{c.certification_name}</span>
                    {c.expiry_date && (
                      <span className="text-xs text-muted-foreground">
                        Expires {format(new Date(c.expiry_date), "MMM yyyy")}
                      </span>
                    )}
                  </div>
                  {c.is_verified && <Badge variant="default" className="text-[10px]">Verified</Badge>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader><CardTitle className="text-base">Notification Preferences</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {NOTIF_KEYS.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between">
              <Label className="font-normal">{label}</Label>
              <Switch
                checked={notifPrefs[key] ?? true}
                onCheckedChange={(v) => handleNotifToggle(key, v)}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Account */}
      <Card>
        <CardHeader><CardTitle className="text-base">Account</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Button variant="outline" onClick={() => setPwOpen(true)}>
            Change Password
          </Button>
        </CardContent>
      </Card>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
};

export default WorkerProfile;
